import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import AdditionDetailsModalElements from '../ElementComponents/AdditionDetailsModalElements';
import { generateFormSchema, defaultFieldValues } from './constants';
import { typeModalMapping } from './modalConfig';
import { useExpenseClaimCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimProvider';
import { CLAIM_FORM_TYPES } from '../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';

import './AdditionalDetailsModal.scss';

export default function AdditionalDetailsModal({
  isOpen,
  toggle,
  modelType,
  rowIndex,
  rowData,
  formType,
  checkLastRowAndAdd = null,
}) {
  // Lookup config for this modal type
  const { formMethods: mainFormMethods } = useExpenseClaimCtx();

  const config = useMemo(() => typeModalMapping[modelType] || {}, [modelType]);

  // Build a Yup schema based on modelType
  const validationSchema = generateFormSchema(modelType);

  // Initialize react-hook-form
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultFieldValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const formSubmit = async data => {
    Object.entries(data).forEach(([key, val]) => {
      mainFormMethods.setValue(`claims.${rowIndex}.${key}`, val, {
        shouldValidate: true,
      });
    });
    await mainFormMethods.trigger(`claims.${rowIndex}`);
    checkLastRowAndAdd();
    toggle();
  };

  useEffect(() => {
    if (rowData && isOpen) {
      const initialValues = Object.entries(config).reduce(
        (acc, [fieldName, configObj]) => {
          acc[fieldName] = rowData[configObj.fieldName] ?? '';
          return acc;
        },
        {},
      );
      reset(initialValues);
      return;
    }
    if (!isOpen) return;
    reset(defaultFieldValues);
  }, [rowData, config, reset, isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="border-bottom py-3">
        Additional Details
      </ModalHeader>

      <FormProvider {...methods}>
        <ModalBody>
          {Object.entries(config).map(([fieldName, fieldConfig]) => (
            <div key={fieldName} className="mb-3">
              <AdditionDetailsModalElements
                formMethods={methods}
                name={fieldConfig?.fieldName}
                type={fieldConfig?.type}
                config={fieldConfig}
                rowData={rowData}
                formType={formType}
                title={fieldConfig?.title}
              />
            </div>
          ))}
        </ModalBody>
        {formType !== CLAIM_FORM_TYPES.VIEW && (
          <ModalFooter>
            <div className="d-flex justify-content-between w-100">
              <Button
                className="additional-modal-cancel-button"
                onClick={toggle}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="additional-modal-save-button"
                onClick={handleSubmit(formSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </ModalFooter>
        )}
      </FormProvider>
    </Modal>
  );
}
